"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ArchiveBoxIcon,
  TruckIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

const CONTENT = {
  fr: {
    h1: "Débarras de cave, grenier et garage à Bruxelles",
    heroText:
      "Une cave, un grenier ou un garage encombrés depuis des années, sans avoir besoin de vider le reste du bien : nous intervenons dans toute la Région bruxelloise pour ces espaces précis, avec tri des objets valorisables et évacuation complète du reste.",
    freeQuote: "Demander un devis gratuit",
    intro: {
      title: "Un débarras ciblé, sans devoir vider tout le bien",
      p1before:
        "Cave, grenier et garage sont souvent les espaces où s'accumulent le plus longtemps des objets qu'on ne trie jamais vraiment : outillage, meubles remisés, cartons de déménagement jamais rouverts, vélos hors d'usage, décorations saisonnières. Contrairement à un débarras de ",
      p1link1: "maison complète",
      p1mid: " ou d'",
      p1link2: "appartement",
      p1end: ", cette intervention peut porter uniquement sur l'un de ces espaces, ou sur les trois combinés si votre bien le nécessite.",
      p2: "Le principe reste identique quel que soit le volume : nous examinons ce qui s'y trouve, identifions ce qui peut être racheté, puis évacuons le reste dans les filières adaptées. Un devis gratuit est établi au préalable, sur base du volume réel et des conditions d'accès.",
      p3: "Ces espaces annexes sont aussi ceux que l'on reporte le plus longtemps : contrairement à une pièce de vie, une cave ou un grenier ne « dérangent » pas au quotidien, jusqu'au jour où une vente, une succession ou des travaux imposent d'y remettre de l'ordre rapidement. C'est précisément dans ce type de situation qu'une intervention ciblée, sans mobiliser toute la maison, prend tout son sens.",
    },
    types: {
      title: "Trois espaces, trois logiques différentes",
      p: "Une cave, un grenier et un garage n'accumulent pas le même type d'objets et ne posent pas les mêmes contraintes d'intervention. Les distinguer permet d'anticiper le déroulement du chantier.",
      items: [
        { title: "Cave", text: "Souvent voûtée dans le bâti bruxellois ancien, humide, avec un accès par escalier étroit. On y retrouve typiquement du vin, des conserves, des cartons, du mobilier de réserve et parfois d'anciens outils." },
        { title: "Grenier", text: "Accessible par escalier ou trappe, parfois sous toiture non isolée. Malles, vêtements, jouets, documents anciens et meubles démontés s'y accumulent souvent sur plusieurs générations." },
        { title: "Garage", text: "En sous-sol d'immeuble, en box séparé ou attenant à la maison. Souvent transformé en atelier ou dépôt : outillage, vélos, pneus, matériel de bricolage et mobilier encombrant." },
      ],
    },
    situations: {
      title: "Dans quels cas ce service est pertinent",
      items: [
        "Cave voûtée encombrée depuis des décennies",
        "Grenier à vider avant des travaux de rénovation",
        "Garage transformé en dépôt au fil des années",
        "Objets oubliés découverts lors d'une succession",
        "Préparation d'un bien avant sa mise en vente",
        "Remise en état d'un espace commun avant location",
        "Box ou parking encombré par du mobilier ou des cartons",
        "Cave à vider avant une inspection ou un contrôle technique",
      ],
    },
    steps: {
      title: "Comment se déroule l'intervention",
      items: [
        { n: "1", title: "Description de l'espace", text: "Par téléphone ou via le formulaire, avec ou sans photos : surface approximative, type d'accès, ce que vous souhaitez conserver." },
        { n: "2", title: "Devis gratuit", text: "Nous établissons une estimation sur base du volume à évacuer et des conditions d'accès, sans engagement de votre part." },
        { n: "3", title: "Tri sur place", text: "Le jour de l'intervention, nous examinons les objets présents avant de les évacuer, pour identifier ce qui mérite d'être racheté." },
        { n: "4", title: "Évacuation complète", text: "Le reste est chargé, transporté et orienté vers les filières adaptées : recyclage, don ou déchet selon la nature des objets." },
      ],
    },
    prix: {
      title: "Ce qui influence le prix et la durée",
      p1: "Deux caves de même surface peuvent demander un temps très différent : c'est le volume réellement entassé, et non les mètres carrés, qui détermine l'ampleur du travail. L'accès joue aussi un rôle direct — une cave accessible depuis la rue se vide plus vite qu'un grenier atteignable uniquement par une trappe et une échelle, ou qu'un garage en sous-sol desservi par une rampe étroite.",
      p2before: "Le type d'objets présents compte également : des cartons et du mobilier léger se manipulent plus rapidement que des meubles volumineux, des gravats ou du matériel lourd. Chaque situation étant différente, l'estimation précise se fait toujours sur base de votre cas réel — voir notre page ",
      p2link1: "tarifs et facteurs de prix",
      p2mid: " ou notre article sur la ",
      p2link2: "durée d'un débarras de cave ou grenier",
      p2end: ".",
    },
    rachat: {
      badge: "Tri et valorisation",
      title: "Des espaces souvent riches en surprises",
      p: "Une cave ou un grenier jamais réellement triés peuvent contenir des objets qu'on a oubliés depuis longtemps : mobilier ancien, outillage de qualité, malles, vaisselle, collections diverses. Avant toute évacuation, nous examinons systématiquement ce qui s'y trouve.",
      items: [
        "Meubles anciens ou de style remisés",
        "Outillage et matériel de qualité",
        "Objets de collection (livres, vinyles, monnaies)",
        "Vaisselle, argenterie et bibelots anciens",
      ],
      cta: "En savoir plus sur le rachat d'objets",
    },
    acces: {
      title: "Accès et préparation",
      p1: "Chaque bien bruxellois a ses particularités : cave voûtée avec escalier étroit, grenier accessible par trappe, garage en sous-sol d'un immeuble avec syndic, box de parking partagé. Nous en tenons compte pour organiser l'intervention — matériel adapté, créneau horaire, stationnement du véhicule à proximité.",
      p2: "De votre côté, il suffit d'identifier au préalable ce que vous souhaitez conserver et de nous signaler tout élément particulier (accès restreint, horaires de copropriété, objets fragiles). Le reste de l'organisation est pris en charge le jour de l'intervention.",
    },
    pourquoi: {
      title: "Pourquoi passer par ce service",
      items: [
        "Devis gratuit établi avant toute intervention",
        "Intervention possible sur un seul espace ou plusieurs combinés",
        "Tri des objets valorisables avant évacuation",
        "Organisation adaptée aux contraintes d'accès bruxelloises",
        "Évacuation orientée vers les filières adaptées",
        "Intervention dans les 19 communes de Bruxelles",
      ],
      cta: "Voir des exemples d'interventions réalisées",
    },
    faq: {
      title: "Questions fréquentes",
      items: [
        {
          q: "Faut-il vider toute la maison pour faire appel à vous ?",
          a: "Non. Nous intervenons aussi bien pour une cave, un grenier ou un garage isolé que dans le cadre d'un débarras plus large. Si vous n'avez qu'un seul espace à vider, le devis porte uniquement sur celui-ci.",
        },
        {
          q: "Comment estimez-vous le prix d'un débarras de cave ou grenier ?",
          a: "Le prix dépend du volume réel à évacuer, de la nature des objets et des conditions d'accès (escalier, trappe, distance jusqu'au véhicule). Nous établissons un devis gratuit après avoir vu l'espace, sur place ou par photos, pour éviter toute mauvaise surprise.",
        },
        {
          q: "Que faites-vous des objets encore en bon état ?",
          a: "Nous les examinons avant toute évacuation. Meubles anciens, outillage de qualité, objets de collection ou de valeur peuvent être rachetés et déduits du devis. Voir notre page rachat d'objets pour le détail.",
        },
        {
          q: "Un grenier accessible uniquement par une trappe pose-t-il problème ?",
          a: "Cela ralentit la manutention mais ne nous empêche pas d'intervenir. Nous en tenons compte dans l'organisation du chantier et, si nécessaire, dans l'estimation du prix communiquée au préalable.",
        },
        {
          q: "Que devient ce qui n'a pas de valeur marchande ?",
          a: "Les objets sans valeur de revente sont évacués et orientés vers les filières adaptées : recyclage, don lorsque c'est pertinent, ou déchet selon leur nature. Le tri se fait avant l'évacuation, pas après.",
        },
        {
          q: "Puis-je demander un débarras de garage seul ?",
          a: "Oui. Un garage transformé en dépôt au fil des années, avec vélos, outillage, meubles ou cartons oubliés, peut être vidé indépendamment du reste du bien. Le principe reste le même : estimation gratuite, puis évacuation complète.",
        },
        {
          q: "Intervenez-vous dans toutes les communes bruxelloises ?",
          a: "Oui, dans les 19 communes de la Région de Bruxelles-Capitale. Les contraintes locales (stationnement, immeubles avec syndic, ruelles étroites) sont prises en compte au moment de convenir du créneau d'intervention.",
        },
      ],
    },
    ctaFinal: {
      title: "Une cave, un grenier ou un garage à vider ?",
      p: "Décrivez-nous votre espace, avec ou sans photos, et recevez un devis gratuit. Vous restez libre d'accepter ou non.",
      footerBefore: "Besoin d'un débarras plus large ? ",
      footerLink1: "Consultez nos pages par commune",
      footerMid: " ou ",
      footerLink2: "contactez-nous directement",
      footerEnd: ".",
    },
  },
  nl: {
    h1: "Kelder, zolder en garage ontruimen in Brussel",
    heroText:
      "Een kelder, zolder of garage die al jaren vol staat, zonder dat de rest van het pand moet worden leeggemaakt: wij komen tussen in het hele Brussels Gewest voor deze specifieke ruimtes, met sortering van waardevolle voorwerpen en volledige afvoer van de rest.",
    freeQuote: "Vraag een gratis offerte aan",
    intro: {
      title: "Een gerichte ontruiming, zonder het hele pand te moeten leegmaken",
      p1before:
        "Kelder, zolder en garage zijn vaak de ruimtes waar het langst voorwerpen ophopen die nooit echt gesorteerd worden: gereedschap, opgeborgen meubels, verhuisdozen die nooit meer geopend zijn, onbruikbare fietsen, seizoensdecoratie. In tegenstelling tot een ontruiming van een ",
      p1link1: "volledig huis",
      p1mid: " of ",
      p1link2: "appartement",
      p1end: ", kan deze interventie enkel betrekking hebben op één van deze ruimtes, of op alle drie samen als uw pand dat vereist.",
      p2: "Het principe blijft hetzelfde ongeacht het volume: wij bekijken wat zich er bevindt, identificeren wat kan worden ingekocht, en voeren vervolgens de rest af naar de geschikte kanalen. Een gratis offerte wordt vooraf opgesteld, op basis van het werkelijke volume en de toegangsvoorwaarden.",
      p3: "Deze bijruimtes zijn ook diegene die het langst worden uitgesteld: in tegenstelling tot een leefruimte 'storen' een kelder of zolder niet in het dagelijkse leven, tot op de dag dat een verkoop, een erfenis of verbouwingswerken vereisen dat er snel orde op zaken wordt gesteld. Precies in dat soort situaties krijgt een gerichte interventie, zonder het hele huis te mobiliseren, haar volle betekenis.",
    },
    types: {
      title: "Drie ruimtes, drie verschillende logica's",
      p: "Een kelder, een zolder en een garage verzamelen niet hetzelfde soort voorwerpen en stellen niet dezelfde interventiebeperkingen. Door ze te onderscheiden kan het verloop van de werf beter worden ingeschat.",
      items: [
        { title: "Kelder", text: "Vaak gewelfd in de oude Brusselse bebouwing, vochtig, met toegang via een smalle trap. Hier vindt men doorgaans wijn, conserven, dozen, reservemeubilair en soms oud gereedschap." },
        { title: "Zolder", text: "Toegankelijk via een trap of een luik, soms onder een niet-geïsoleerd dak. Koffers, kleding, speelgoed, oude documenten en gedemonteerde meubels stapelen zich er vaak op over meerdere generaties." },
        { title: "Garage", text: "In de kelderverdieping van een gebouw, in een aparte box of aangebouwd aan het huis. Vaak omgevormd tot atelier of opslagplaats: gereedschap, fietsen, banden, doe-het-zelfmateriaal en omvangrijk meubilair." },
      ],
    },
    situations: {
      title: "In welke gevallen is deze dienst relevant",
      items: [
        "Gewelfde kelder die al decennialang vol staat",
        "Zolder leegmaken vóór renovatiewerken",
        "Garage die in de loop der jaren is omgevormd tot opslagplaats",
        "Vergeten voorwerpen ontdekt bij een erfenis",
        "Voorbereiding van een pand vóór de verkoop",
        "Herstel van een gemeenschappelijke ruimte vóór verhuur",
        "Box of parking vol met meubilair of dozen",
        "Kelder leegmaken vóór een inspectie of technische controle",
      ],
    },
    steps: {
      title: "Hoe verloopt de interventie",
      items: [
        { n: "1", title: "Beschrijving van de ruimte", text: "Telefonisch of via het formulier, met of zonder foto's: geschatte oppervlakte, type toegang, wat u wilt bewaren." },
        { n: "2", title: "Gratis offerte", text: "Wij stellen een schatting op op basis van het af te voeren volume en de toegangsvoorwaarden, zonder enige verplichting van uw kant." },
        { n: "3", title: "Sortering ter plaatse", text: "Op de dag van de interventie bekijken wij de aanwezige voorwerpen vóór we ze afvoeren, om te bepalen wat het waard is om in te kopen." },
        { n: "4", title: "Volledige afvoer", text: "De rest wordt geladen, vervoerd en naar de geschikte kanalen geleid: recyclage, schenking of afval naargelang de aard van de voorwerpen." },
      ],
    },
    prix: {
      title: "Wat de prijs en de duur beïnvloedt",
      p1: "Twee kelders met dezelfde oppervlakte kunnen een heel andere tijd vragen: het is het werkelijk opgestapelde volume, en niet de vierkante meters, dat de omvang van het werk bepaalt. De toegang speelt ook een directe rol — een kelder die vanaf de straat bereikbaar is, wordt sneller leeggemaakt dan een zolder die enkel via een luik en een ladder bereikbaar is, of een ondergrondse garage bediend door een smalle helling.",
      p2before: "Het type aanwezige voorwerpen telt ook mee: dozen en licht meubilair worden sneller gehanteerd dan omvangrijke meubels, puin of zwaar materiaal. Aangezien elke situatie anders is, gebeurt de precieze schatting altijd op basis van uw werkelijke geval — zie onze pagina ",
      p2link1: "tarieven en prijsfactoren",
      p2mid: " of ons artikel over de ",
      p2link2: "duur van een kelder- of zolderontruiming",
      p2end: ".",
    },
    rachat: {
      badge: "Sortering en valorisatie",
      title: "Ruimtes die vaak vol verrassingen zitten",
      p: "Een kelder of zolder die nooit echt gesorteerd is, kan voorwerpen bevatten die al lang vergeten zijn: antiek meubilair, kwaliteitsgereedschap, koffers, servies, diverse verzamelingen. Vóór elke afvoer bekijken wij systematisch wat zich er bevindt.",
      items: [
        "Opgeborgen antieke of stijlmeubels",
        "Gereedschap en materiaal van goede kwaliteit",
        "Verzamelobjecten (boeken, vinylplaten, munten)",
        "Servies, zilverwerk en antieke snuisterijen",
      ],
      cta: "Meer weten over de inkoop van voorwerpen",
    },
    acces: {
      title: "Toegang en voorbereiding",
      p1: "Elk Brussels pand heeft zijn eigenheden: gewelfde kelder met smalle trap, zolder bereikbaar via een luik, ondergrondse garage van een gebouw met syndicus, gedeelde parkeerbox. Wij houden hier rekening mee bij de organisatie van de interventie — aangepast materiaal, tijdslot, parkeren van het voertuig in de buurt.",
      p2: "Van uw kant volstaat het om vooraf te bepalen wat u wilt bewaren en ons elk bijzonder element te melden (beperkte toegang, openingstijden van de mede-eigendom, breekbare voorwerpen). De rest van de organisatie wordt op de dag van de interventie geregeld.",
    },
    pourquoi: {
      title: "Waarom voor deze dienst kiezen",
      items: [
        "Gratis offerte, opgesteld vóór elke interventie",
        "Interventie mogelijk voor één enkele ruimte of meerdere gecombineerd",
        "Sortering van waardevolle voorwerpen vóór de afvoer",
        "Organisatie afgestemd op de Brusselse toegangsbeperkingen",
        "Afvoer gericht naar de geschikte kanalen",
        "Interventie in de 19 gemeenten van Brussel",
      ],
      cta: "Bekijk voorbeelden van uitgevoerde interventies",
    },
    faq: {
      title: "Veelgestelde vragen",
      items: [
        {
          q: "Moet ik het hele huis leegmaken om een beroep op u te doen?",
          a: "Nee. Wij komen zowel tussen voor een geïsoleerde kelder, zolder of garage als in het kader van een grotere ontruiming. Als u slechts één ruimte moet leegmaken, heeft de offerte enkel betrekking op die ruimte.",
        },
        {
          q: "Hoe schat u de prijs van een kelder- of zolderontruiming in?",
          a: "De prijs hangt af van het werkelijke volume dat moet worden afgevoerd, de aard van de voorwerpen en de toegangsvoorwaarden (trap, luik, afstand tot het voertuig). Wij stellen een gratis offerte op nadat we de ruimte hebben gezien, ter plaatse of via foto's, om elke onaangename verrassing te vermijden.",
        },
        {
          q: "Wat doet u met voorwerpen die nog in goede staat zijn?",
          a: "Wij bekijken ze vóór elke afvoer. Antieke meubels, kwaliteitsgereedschap, verzamelobjecten of waardevolle voorwerpen kunnen worden ingekocht en afgetrokken van de offerte. Zie onze pagina inkoop van voorwerpen voor meer details.",
        },
        {
          q: "Is een zolder die enkel via een luik bereikbaar is een probleem?",
          a: "Dat vertraagt het transport, maar belet ons niet om tussen te komen. Wij houden hiermee rekening bij de organisatie van de werf en, indien nodig, bij de vooraf meegedeelde prijsschatting.",
        },
        {
          q: "Wat gebeurt er met voorwerpen zonder marktwaarde?",
          a: "Voorwerpen zonder wederverkoopwaarde worden afgevoerd en naar de geschikte kanalen geleid: recyclage, schenking indien relevant, of afval naargelang hun aard. De sortering gebeurt vóór de afvoer, niet erna.",
        },
        {
          q: "Kan ik enkel een garage laten ontruimen?",
          a: "Ja. Een garage die in de loop der jaren is omgevormd tot opslagplaats, met vergeten fietsen, gereedschap, meubels of dozen, kan onafhankelijk van de rest van het pand worden leeggemaakt. Het principe blijft hetzelfde: gratis schatting, gevolgd door volledige afvoer.",
        },
        {
          q: "Komt u tussen in alle Brusselse gemeenten?",
          a: "Ja, in de 19 gemeenten van het Brussels Hoofdstedelijk Gewest. De lokale beperkingen (parkeren, gebouwen met syndicus, smalle steegjes) worden in rekening gebracht bij het afspreken van het interventieslot.",
        },
      ],
    },
    ctaFinal: {
      title: "Een kelder, zolder of garage te ontruimen?",
      p: "Beschrijf ons uw ruimte, met of zonder foto's, en ontvang een gratis offerte. U blijft vrij om deze al dan niet te aanvaarden.",
      footerBefore: "Nood aan een ruimere ontruiming? ",
      footerLink1: "Bekijk onze pagina's per gemeente",
      footerMid: " of ",
      footerLink2: "neem rechtstreeks contact met ons op",
      footerEnd: ".",
    },
  },
  en: {
    h1: "Cellar, Attic and Garage Clearance in Brussels",
    heroText:
      "A cellar, attic or garage cluttered for years, without needing to clear the rest of the property: we work across the entire Brussels Region for these specific spaces, sorting valuable items and fully removing the rest.",
    freeQuote: "Request a free quote",
    intro: {
      title: "A targeted clearance, without emptying the whole property",
      p1before:
        "Cellars, attics and garages are often the spaces where items pile up longest without ever really being sorted: tools, stored-away furniture, moving boxes never reopened, unusable bikes, seasonal decorations. Unlike clearing a ",
      p1link1: "full house",
      p1mid: " or ",
      p1link2: "apartment",
      p1end: ", this service can cover just one of these spaces, or all three combined if your property requires it.",
      p2: "The principle stays the same regardless of the volume: we examine what's there, identify what can be bought back, then remove the rest through the appropriate channels. A free quote is drawn up beforehand, based on the actual volume and access conditions.",
      p3: "These ancillary spaces are also the ones people put off the longest: unlike a living room, a cellar or attic doesn't “get in the way” day to day, until the day a sale, an estate settlement or renovation work requires putting them back in order quickly. It's precisely in this kind of situation that a targeted intervention, without mobilising the whole house, makes the most sense.",
    },
    types: {
      title: "Three spaces, three different logics",
      p: "A cellar, an attic and a garage don't accumulate the same type of items and don't pose the same intervention constraints. Distinguishing between them helps anticipate how the job will unfold.",
      items: [
        { title: "Cellar", text: "Often vaulted in older Brussels buildings, damp, accessed via a narrow staircase. Typically holds wine, preserves, boxes, spare furniture and sometimes old tools." },
        { title: "Attic", text: "Accessed via a staircase or hatch, sometimes under an uninsulated roof. Trunks, clothing, toys, old documents and dismantled furniture often accumulate here over several generations." },
        { title: "Garage", text: "In a building's basement, a separate box or attached to the house. Often turned into a workshop or storage space: tools, bikes, tyres, DIY equipment and bulky furniture." },
      ],
    },
    situations: {
      title: "When this service makes sense",
      items: [
        "Vaulted cellar cluttered for decades",
        "Attic to be cleared before renovation work",
        "Garage turned into storage over the years",
        "Forgotten items discovered during an estate settlement",
        "Preparing a property before it's put up for sale",
        "Restoring a shared space before renting it out",
        "Box or parking space cluttered with furniture or boxes",
        "Cellar to be cleared before an inspection or technical control",
      ],
    },
    steps: {
      title: "How the service unfolds",
      items: [
        { n: "1", title: "Description of the space", text: "By phone or via the form, with or without photos: approximate surface area, type of access, what you wish to keep." },
        { n: "2", title: "Free quote", text: "We draw up an estimate based on the volume to be removed and the access conditions, with no obligation on your part." },
        { n: "3", title: "On-site sorting", text: "On the day of the intervention, we examine the items present before removing them, to identify what's worth buying back." },
        { n: "4", title: "Full removal", text: "The rest is loaded, transported and directed to the appropriate channels: recycling, donation or waste depending on the nature of the items." },
      ],
    },
    prix: {
      title: "What influences price and duration",
      p1: "Two cellars of the same size can require very different amounts of time: it's the volume actually piled up, not the square footage, that determines the scale of the job. Access also plays a direct role — a cellar accessible from the street empties faster than an attic reachable only via a hatch and a ladder, or an underground garage served by a narrow ramp.",
      p2before: "The type of items present also matters: boxes and light furniture are handled faster than bulky furniture, rubble or heavy equipment. Since every situation is different, the precise estimate is always based on your actual case — see our ",
      p2link1: "prices and pricing factors",
      p2mid: " page or our article on the ",
      p2link2: "duration of a cellar or attic clearance",
      p2end: ".",
    },
    rachat: {
      badge: "Sorting and valorisation",
      title: "Spaces often full of surprises",
      p: "A cellar or attic that has never really been sorted can hold items long forgotten: antique furniture, quality tools, trunks, tableware, various collections. Before any removal, we systematically examine what's there.",
      items: [
        "Stored antique or period furniture",
        "Quality tools and equipment",
        "Collectible items (books, vinyl records, coins)",
        "Tableware, silverware and antique curios",
      ],
      cta: "Learn more about item buyback",
    },
    acces: {
      title: "Access and preparation",
      p1: "Every Brussels property has its own quirks: a vaulted cellar with a narrow staircase, an attic reached via a hatch, an underground garage in a building with a syndic, a shared parking box. We take this into account when organising the intervention — suitable equipment, time slot, parking the vehicle nearby.",
      p2: "On your side, it's enough to decide in advance what you want to keep and to let us know about anything particular (restricted access, co-ownership hours, fragile items). The rest of the organisation is taken care of on the day of the intervention.",
    },
    pourquoi: {
      title: "Why choose this service",
      items: [
        "Free quote drawn up before any intervention",
        "Service available for a single space or several combined",
        "Sorting of valuable items before removal",
        "Organisation adapted to Brussels access constraints",
        "Removal directed to the appropriate channels",
        "Service across the 19 communes of Brussels",
      ],
      cta: "See examples of completed interventions",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "Do I need to clear the whole house to use your service?",
          a: "No. We intervene for a standalone cellar, attic or garage just as we do for a larger clearance. If you only have one space to clear, the quote covers only that space.",
        },
        {
          q: "How do you estimate the price of a cellar or attic clearance?",
          a: "The price depends on the actual volume to be removed, the nature of the items and the access conditions (stairs, hatch, distance to the vehicle). We draw up a free quote after seeing the space, on site or via photos, to avoid any unpleasant surprises.",
        },
        {
          q: "What do you do with items still in good condition?",
          a: "We examine them before any removal. Antique furniture, quality tools, collectible or valuable items may be bought back and deducted from the quote. See our item buyback page for details.",
        },
        {
          q: "Is an attic only accessible via a hatch a problem?",
          a: "It slows down the handling but doesn't prevent us from intervening. We take this into account when organising the job and, if necessary, in the price estimate communicated beforehand.",
        },
        {
          q: "What happens to items with no market value?",
          a: "Items with no resale value are removed and directed to the appropriate channels: recycling, donation when relevant, or waste depending on their nature. Sorting happens before removal, not after.",
        },
        {
          q: "Can I request a garage-only clearance?",
          a: "Yes. A garage turned into storage over the years, with forgotten bikes, tools, furniture or boxes, can be cleared independently of the rest of the property. The principle remains the same: free estimate, then full removal.",
        },
        {
          q: "Do you cover all Brussels communes?",
          a: "Yes, across all 19 communes of the Brussels-Capital Region. Local constraints (parking, buildings with a syndic, narrow alleys) are taken into account when agreeing on the intervention time slot.",
        },
      ],
    },
    ctaFinal: {
      title: "A cellar, attic or garage to clear?",
      p: "Describe your space to us, with or without photos, and receive a free quote. You remain free to accept it or not.",
      footerBefore: "Need a wider clearance? ",
      footerLink1: "Browse our pages by commune",
      footerMid: " or ",
      footerLink2: "contact us directly",
      footerEnd: ".",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 480px at 15% -10%, rgba(63,108,81,0.08), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1200px] px-6 py-20 lg:px-10 lg:py-28">
          <h1 className="max-w-3xl font-serif text-4xl font-black leading-[1.1] md:text-5xl">
            {c.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* INTRO / REPONSE DIRECTE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.intro.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.intro.p1before}
            <Link href="/debarras-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.intro.p1link1}
            </Link>
            {c.intro.p1mid}
            <Link href="/debarras-appartement" className="font-semibold text-accent underline underline-offset-4">
              {c.intro.p1link2}
            </Link>
            {c.intro.p1end}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.intro.p2}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.intro.p3}
          </p>
        </div>
      </section>

      {/* TYPES D'ESPACES */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.types.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.types.p}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {c.types.items.map((item) => (
              <div key={item.title} className="rounded-2xl border border-sand-line bg-white p-6">
                <p className="font-serif text-lg font-bold text-ink">{item.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.situations.title}
          </h2>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.situations.items.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 rounded-2xl border border-sand-line bg-sand p-4 text-sm font-semibold text-ink"
              >
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DEROULEMENT */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.steps.title}
          </h2>
          <ol className="mt-8 space-y-6">
            {c.steps.items.map((step) => (
              <li key={step.n} className="flex gap-5 rounded-3xl border border-sand-line bg-white p-6">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink font-serif font-bold text-white">
                  {step.n}
                </span>
                <div>
                  <p className="font-bold text-ink">{step.title}</p>
                  <p className="mt-1 text-sm text-neutral-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FACTEURS PRIX / DUREE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <ClockIcon className="h-8 w-8 text-accent" />
            <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.prix.title}
            </h2>
          </div>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.prix.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.prix.p2before}
            <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
              {c.prix.p2link1}
            </Link>
            {c.prix.p2mid}
            <Link
              href="/blog/duree-debarras-cave-grenier"
              className="font-semibold text-accent underline underline-offset-4"
            >
              {c.prix.p2link2}
            </Link>
            {c.prix.p2end}
          </p>
        </div>
      </section>

      {/* RACHAT D'OBJETS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="rounded-[2rem] border border-sage/25 bg-sage/5 p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-sage">
              <GiftIcon className="h-4 w-4" />
              {c.rachat.badge}
            </div>
            <h2 className="mt-4 font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.rachat.title}
            </h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              {c.rachat.p}
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {c.rachat.items.map((v) => (
                <li key={v} className="flex items-start gap-2 text-sm font-semibold text-ink">
                  <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-sage" />
                  {v}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <Link
                href="/rachat-objets"
                className="inline-flex items-center gap-2 font-bold text-sage hover:text-ink"
              >
                {c.rachat.cta}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ACCES / LOGISTIQUE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <TruckIcon className="h-8 w-8 text-accent" />
            <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.acces.title}
            </h2>
          </div>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.acces.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.acces.p2}
          </p>
        </div>
      </section>

      {/* POURQUOI CHOISIR */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <ArchiveBoxIcon className="h-8 w-8 text-accent" />
            <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.pourquoi.title}
            </h2>
          </div>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.pourquoi.items.map((b) => (
              <li key={b} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <span className="text-sm font-semibold text-ink">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Link
              href="/realisations"
              className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark"
            >
              {c.pourquoi.cta}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">{c.faq.title}</h2>
          <div className="mt-8 space-y-4">
            {c.faq.items.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[1000px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.ctaFinal.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-neutral-700">
            {c.ctaFinal.p}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.freeQuote}
            </CtaLink>
          </div>
          <p className="mt-8 text-sm text-neutral-500">
            {c.ctaFinal.footerBefore}
            <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.ctaFinal.footerLink1}
            </Link>
            {c.ctaFinal.footerMid}
            <Link href="/contact" className="font-semibold text-accent underline underline-offset-4">
              {c.ctaFinal.footerLink2}
            </Link>
            {c.ctaFinal.footerEnd}
          </p>
        </div>
      </section>

      <Contact />
    </div>
  );
}
